import { useState } from 'react';
import type { PersonId, Segment } from '@shared/types.ts';
import { whoIsGoing } from '@shared/people.ts';
import { getStore, useAppState } from '../../data/store.ts';
import { Sheet, SheetHeader } from '../../components/ui.tsx';

/** M / N / M N, "+ Sam" for anyone else, or "Who's going?" when nothing says. */
export function WhoChips({ seg, onGold }: { seg: Segment; onGold?: boolean }) {
  const people = useAppState((s) => s.people);
  const [picking, setPicking] = useState(false);
  const going = whoIsGoing(seg, people);

  if (!going.known) {
    return (
      <>
        <button type="button" className={`who-ask${onGold ? ' on-gold' : ''}`} onClick={() => setPicking(true)}>
          Who's going?
        </button>
        {picking && <WhoPicker seg={seg} onClose={() => setPicking(false)} />}
      </>
    );
  }
  const names = [...going.people.map((id) => people.find((p) => p.id === id)?.name ?? id), ...going.others].join(', ');
  return (
    <span className="who" aria-label={`Going: ${names}`}>
      {going.people.length > 0 && (
        <span className="avatar-pair">
          {going.people.map((id) => (
            <span key={id} className={`avatar sm ${id}`} aria-hidden="true">
              {people.find((p) => p.id === id)?.initial}
            </span>
          ))}
        </span>
      )}
      {going.others.length > 0 && <span className="who-more">+ {going.others.join(', ')}</span>}
    </span>
  );
}

/** Michael / Natalie / Both, saved for every leg with the same confirmation code. */
export function WhoPicker({ seg, onClose }: { seg: Segment; onClose: () => void }) {
  const store = getStore();
  const people = useAppState((s) => s.people);
  const pick = (who: PersonId[]) => {
    store.setWho(seg.id, who);
    onClose();
  };
  return (
    <Sheet title="Who's going?" onClose={onClose}>
      <SheetHeader title="Who's going?" subtitle={seg.confirmation ? `Every flight under ${seg.confirmation}` : undefined} onClose={onClose} />
      <div className="stack" style={{ padding: '0 20px 20px' }}>
        {people.map((p) => (
          <button key={p.id} type="button" className="btn" onClick={() => pick([p.id])}>
            <span className={`avatar sm ${p.id}`}>{p.initial}</span> {p.name}
          </button>
        ))}
        <button type="button" className="btn btn-primary" onClick={() => pick(people.map((p) => p.id))}>
          Both of us
        </button>
      </div>
    </Sheet>
  );
}

/** Michael / Natalie / Both, for the add and edit forms. */
export function WhoField({ value, onChange }: { value: PersonId[]; onChange: (who: PersonId[]) => void }) {
  const people = useAppState((s) => s.people);
  const all = people.map((p) => p.id);
  const options: Array<{ label: string; who: PersonId[] }> = [...people.map((p) => ({ label: p.name, who: [p.id] })), { label: 'Both', who: all }];
  const same = (a: PersonId[], b: PersonId[]) => a.length === b.length && a.every((x) => b.includes(x));
  return (
    <div className="field">
      <span className="label" id="sf-who-label">
        Who's going
      </span>
      <div className="segmented" role="group" aria-labelledby="sf-who-label" id="sf-who">
        {options.map((o) => (
          <button key={o.label} type="button" className={same(value, o.who) ? 'on' : ''} aria-pressed={same(value, o.who)} onClick={() => onChange(o.who)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
